import { cn } from '@/lib/utils'

/**
 * Layout-matching loading placeholders (UX-096).
 *
 * Pick the one shaped like the content that will replace it, so nothing jumps
 * when data arrives. They carry no numbers or labels ("0 today" in a skeleton
 * reads as a real value). Every one announces "Loading" once to screen readers.
 */

function Bar({ className }: { className?: string }) {
  return <div className={cn('shimmer rounded-md', className)} aria-hidden="true" />
}

function Busy({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  )
}

/** Rows of a list (contacts, threads, jobs). */
export function ListSkeleton({ rows = 6, avatar = true, label = 'Loading…', className }: { rows?: number; avatar?: boolean; label?: string; className?: string }) {
  return (
    <Busy label={label} className={cn('ds-tile divide-y divide-white/[0.06] overflow-hidden', className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3.5">
          {avatar && <Bar className="h-10 w-10 shrink-0 rounded-full" />}
          <div className="min-w-0 flex-1 space-y-2">
            <Bar className={cn('h-4', i % 3 === 0 ? 'w-2/5' : i % 3 === 1 ? 'w-1/2' : 'w-1/3')} />
            <Bar className="h-3 w-3/5" />
          </div>
          <Bar className="h-4 w-14 shrink-0" />
        </div>
      ))}
    </Busy>
  )
}

/** A grid of cards (KPI tiles, board columns, settings cards). */
export function CardGridSkeleton({ cards = 4, columns = 'grid-cols-2 lg:grid-cols-4', cardHeight = 'h-28', label = 'Loading…', className }: { cards?: number; columns?: string; cardHeight?: string; label?: string; className?: string }) {
  return (
    <Busy label={label} className={cn('grid gap-3', columns, className)}>
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} className={cn('ds-tile p-4 space-y-3', cardHeight)}>
          <Bar className="h-3 w-1/2" />
          <Bar className="h-7 w-2/3" />
        </div>
      ))}
    </Busy>
  )
}

/** One panel with a heading and a few lines (detail drawers, settings sections). */
export function PanelSkeleton({ lines = 4, label = 'Loading…', className }: { lines?: number; label?: string; className?: string }) {
  return (
    <Busy label={label} className={cn('ds-tile p-5 space-y-3', className)}>
      <Bar className="h-5 w-1/3" />
      {Array.from({ length: lines }).map((_, i) => (
        <Bar key={i} className={cn('h-4', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </Busy>
  )
}

/**
 * Whole-page placeholder: title, toolbar, then list/grid body. The route-level
 * Suspense fallback, and a good default for a page's first load.
 */
export function PageSkeleton({ variant = 'list', label = 'Loading…' }: { variant?: 'list' | 'grid' | 'panel'; label?: string }) {
  return (
    <div className="page-container" data-testid="page-skeleton">
      <Busy label={label}>
        <div className="mb-6 space-y-3">
          <Bar className="h-8 w-48" />
          <Bar className="h-4 w-64 max-w-full" />
        </div>
        <div className="mb-4 flex gap-2">
          <Bar className="h-10 w-28 rounded-full" />
          <Bar className="h-10 w-24 rounded-full" />
          <Bar className="h-10 flex-1 max-w-xs rounded-full" />
        </div>
        <div aria-hidden="true">
          {variant === 'grid' ? (
            <CardGridSkeleton cards={6} columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" cardHeight="h-36" />
          ) : variant === 'panel' ? (
            <div className="space-y-4"><PanelSkeleton /><PanelSkeleton lines={3} /></div>
          ) : (
            <ListSkeleton rows={7} />
          )}
        </div>
      </Busy>
    </div>
  )
}
